import { type ChangeEvent, memo, useCallback, useEffect, useState } from 'react';

import { Textarea } from '@air/primitive-textarea';

import { useShowcaseModal } from '~/components/ShowcaseModal/ShowcaseModalContext';

export const ShowcaseDescriptionSetting = memo(() => {
  const { showcaseProps, updateShowcaseDescription, saveShowcaseDescription, isEditMode, errors, isSaving } =
    useShowcaseModal();
  const [description, setDescription] = useState<string>(showcaseProps.description ?? '');

  useEffect(() => {
    setDescription(showcaseProps.description ?? '');
  }, [showcaseProps.description]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newDescription = e.target.value;
      setDescription(newDescription);
      updateShowcaseDescription(newDescription);
    },
    [updateShowcaseDescription],
  );

  const errorId = 'collection-description-error';
  const hasError = !!errors?.description;

  const handleBlur = useCallback(() => {
    const currentDescription = showcaseProps.description ?? '';
    const hasChanges = description !== currentDescription;

    if (isEditMode && hasChanges && !hasError) {
      saveShowcaseDescription?.();
    }
  }, [description, showcaseProps.description, hasError, isEditMode, saveShowcaseDescription]);

  return (
    <div>
      <Textarea
        size="large"
        value={description}
        data-testid="COLLECTION_DESCRIPTION_SETTING_TEXTAREA"
        onChange={handleChange}
        name="collectionDescription"
        aria-label="Description"
        aria-describedby={hasError ? errorId : undefined}
        className="w-full rounded-8"
        placeholder="Add a description"
        onBlur={handleBlur}
        disabled={isSaving}
        hasError={hasError}
        rows={4}
      />
      {hasError && (
        <p id={errorId} className="ads:text-error text-12 text-red-9">
          {errors?.description}
        </p>
      )}
    </div>
  );
});

ShowcaseDescriptionSetting.displayName = 'ShowcaseDescriptionSetting';
